// libraries
import React, { useState, useEffect, Fragment } from "react";
import { BiFilterAlt } from "react-icons/bi";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
// components
import AppPagesLayout from "components/app-pages-layout";
import AppBreadcrumb from "components/app-breadcrumb";
import AppTabLink from "components/app-tab-link";
import AppReactTable from "components/app-react-table";
import AppPaginate from "components/app-paginate";
import AppDropdownCheckbox from "components/app-dropdown-checkbox";
import AppDualColumnsModal from "components/app-dual-columns-modal";
import AppInputWithLabel from "components/app-input-with-label";
import AppTextArea from "components/app-text-area";
import appToast from "components/app-toast";
import AppModal from "components/app-modal";
import AppAttachmentImage from "components/app-attachment-image";
// common
import { sanitizeError, statusFormat } from "common/utilities";
// service
import api from "services/api";
// hooks
import usePrevious from "hooks/use-previous";
// routes
import pathnames from "routes/pathnames";
// assets
import iconTailedArrowUp from "assets/images/icon-tailed-arrow-up.svg";

const breadcrumb = [
    {
        label: "Claim Management",
    },
    {
        label: "Claim Directory",
        pathname: pathnames.pageClaimDirectory,
    },
    {
        label: "Claim History",
    },
];

const getTableStatusClassNames = (status) => {
    const classNames = ["react-table__status-data"];
    if (status.toLowerCase() === "approved" || status.toLowerCase() === "approve") classNames.push("react-table__status--green");
    if (status.toLowerCase() === "rejected" || status.toLowerCase() === "reject") classNames.push("react-table__status--red");

    return classNames.join(" ");
};

const getStatusColor = (status) => {
    if (status === "reject" || status === "rejected") {
        return "red";
    } else if (status === "approve" || status === "approved") {
        return "green";
    } else if (status === "pending") {
        return "orange";
    } else {
        return "";
    }
};

const tableStatusFilter = [
    { label: "Approved", name: "approved", value: "approve" },
    { label: "Rejected", name: "rejected", value: "reject" },
];

const attachmentSrcInitialState = {
    file: "",
    type: "",
};

let statusToFilter = [];

const selectedClaimInitialValues = {
    amount: "",
    approveAmount: "",
    attachment: "",
    claimType: "",
    desc: "",
    filePath: "",
    id: "",
    rejectReason: "",
    remark: "",
    staffId: "",
    status: "",
    submitDate: "",
    userName: "",
    fileType: "",
};

const PageClaimHistory = () => {
    const [statusFilterOpen, setStatusFilterOpen] = useState(false);
    const { pathStaffId } = useParams();
    const staffId = pathStaffId.replace("value_", "");
    const history = useHistory();
    const [tableSearchFilter, setTableSearchFilter] = useState({
        pageNo: 0,
        pageSize: 5,
        sortBy: "",
        sortDir: "asc",
        claimStatus: "",
        filter: "",
        staffId: ("000" + staffId).slice(-3),
    });
    const [tableData, setTableData] = useState({
        content: [],
        last: null,
        pageNo: 0,
        pageSize: 5,
        totalElements: null,
        totalPages: 1,
    });
    const prevValueSortHeader = usePrevious({
        sortBy: tableSearchFilter.sortBy,
        sortDir: tableSearchFilter.sortDir,
    });
    const [statusFilter, setStatusFilter] = useState({
        approved: false,
        rejected: false,
    });
    const [workingStatus, setWorkingStatus] = useState({
        staffId: "",
        userName: "",
        workingStatus: "",
    });
    const [selectedClaim, setSelectedClaim] = useState(selectedClaimInitialValues);
    const [modalIsOpenSequence, setModalIsOpenSequence] = useState([]);
    const [attachmentSrc, setAttachmentSrc] = useState(attachmentSrcInitialState);

    const appTabLink = [
        {
            label: "Claim Summary",
            pathname: pathnames.pageClaimSummary.replace(":pathStaffId", "value_" + staffId),
        },
        {
            label: "Claim History",
            pathname: pathnames.pageClaimHistory.replace(":pathStaffId", "value_" + staffId),
        },
        {
            label: "Pending Claim",
            pathname: pathnames.pagePendingClaim.replace(":pathStaffId", "value_" + staffId),
        },
    ];

    useEffect(() => {
        const getWorkingStatus = async () => {
            try {
                let payload = ("000" + staffId).slice(-3);
                const response = await api.get.leaveManagementWorkingStatus(payload);
                setWorkingStatus(response.data.result);
            } catch (error) {
                let sanitizedError = sanitizeError(error);
                appToast(sanitizedError, false);
            }
        };
        getWorkingStatus();

        const getClaimHistory = async () => {
            try {
                const response = await api.get.claimManagementClaimHistory(tableSearchFilter);
                setTableData(response.data.result);
            } catch (error) {
                let sanitizedError = sanitizeError(error);
                appToast(sanitizedError, false);
            }
        };
        getClaimHistory();
    }, [history, tableSearchFilter, staffId]);

    const headerArrowRotate = (active) => {
        const classNames = ["react-table__arrow-icon"];
        if (active && tableSearchFilter.sortDir === "desc") classNames.push("react-table__arrow-icon-down");
        if (active) classNames.push("react-table__arrow-icon--active");

        return classNames.join(" ");
    };

    const headerFilterClick = (header) => {
        let column = header.column;
        if (prevValueSortHeader.sortBy === column.searchFilterValue) {
            if (prevValueSortHeader.sortDir === "asc") {
                setTableSearchFilter({
                    ...tableSearchFilter,
                    sortBy: column.searchFilterValue,
                    sortDir: "desc",
                    pageNo: 0,
                });
            } else {
                setTableSearchFilter({
                    ...tableSearchFilter,
                    sortBy: column.searchFilterValue,
                    sortDir: "asc",
                    pageNo: 0,
                });
            }
        } else {
            setTableSearchFilter({
                ...tableSearchFilter,
                sortBy: column.searchFilterValue,
                sortDir: "asc",
                pageNo: 0,
            });
        }
    };

    const tableColumn = [
        {
            id: "claimType",
            Header: (header) => {
                return (
                    <span onClick={() => headerFilterClick(header)} className="react-table__claim-type-header react-table__header">
                        Claim Type
                        <img className={headerArrowRotate(tableSearchFilter.sortBy === header.column.searchFilterValue)} src={iconTailedArrowUp} alt="" />
                    </span>
                );
            },
            Cell: (row) => {
                return <span className="react-table__claim-type-row">{row.row.original.claimType}</span>;
            },
            searchFilterValue: "claim_type",
        },
        {
            id: "submitDate",
            Header: (header) => {
                return (
                    <span onClick={() => headerFilterClick(header)} className="react-table__header">
                        Submit Date
                        <img className={headerArrowRotate(tableSearchFilter.sortBy === header.column.searchFilterValue)} src={iconTailedArrowUp} alt="" />
                    </span>
                );
            },
            maxWidth: 70,
            Cell: (row) => {
                return <span>{moment(row.row.original.submitDate).format("DD/MM/YYYY")}</span>;
            },
            searchFilterValue: "submit_date",
        },
        {
            id: "amount",
            Header: (header) => {
                return (
                    <span onClick={() => headerFilterClick(header)} className="react-table__header">
                        Approved amount (RM)
                        <img className={headerArrowRotate(tableSearchFilter.sortBy === header.column.searchFilterValue)} src={iconTailedArrowUp} alt="" />
                    </span>
                );
            },
            Cell: (row) => {
                return (
                    <div className="react-table__amount">
                        <div>{row.row.original.amount.toFixed(2)}</div>
                    </div>
                );
            },
            maxWidth: 100,
            searchFilterValue: "approve_amount",
        },
        {
            id: "filePath",
            Header: () => {
                return <span className="react-table__header react-table__header--cursor-default">Attachment</span>;
            },
            Cell: (row) => {
                if (row.row.original.filePath) {
                    const fileSplitted = row.row.original.filePath.split(".");
                    const fileType = fileSplitted[fileSplitted.length - 1];
                    return (
                        <div className="react-table__attachment" onClick={(e) => e.stopPropagation()}>
                            <AppAttachmentImage
                                fileType={fileType}
                                onClick={() => {
                                    setAttachmentSrc({
                                        file: row.row.original.filePath,
                                        type: fileType,
                                    });
                                    setModalIsOpenSequence(["MODAL_PDF"]);
                                }}
                            />
                        </div>
                    );
                } else {
                    return null;
                }
            },
            maxWidth: 70,
        },
        {
            id: "claimStatus",
            Header: () => {
                const getStatusClassNames = (statusFilterOpen) => {
                    const classNames = ["react-table__icon-filter"];
                    if (statusFilterOpen) classNames.push("react-table__icon-filter--active");
                    return classNames.join(" ");
                };
                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <span className="react-table__status-header" onClick={() => setStatusFilterOpen(!statusFilterOpen)}>
                            Status
                            <BiFilterAlt className={getStatusClassNames(statusFilterOpen)} />
                        </span>
                        {statusFilterOpen ? (
                            <div className="react-table__dropdown">
                                <AppDropdownCheckbox
                                    dropdownList={tableStatusFilter}
                                    onChange={(item, value) => {
                                        setStatusFilter({
                                            ...statusFilter,
                                            [item.name]: value,
                                        });
                                        if (value) {
                                            statusToFilter.push(item.value);
                                        } else {
                                            statusToFilter = statusToFilter.filter((e) => e !== item.value);
                                        }
                                        if (statusToFilter.length === 1) {
                                            setTableSearchFilter({
                                                ...tableSearchFilter,
                                                pageNo: 0,
                                                filter: statusToFilter[0],
                                            });
                                        } else {
                                            setTableSearchFilter({
                                                ...tableSearchFilter,
                                                pageNo: 0,
                                                filter: "",
                                            });
                                        }
                                    }}
                                    state={statusFilter}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            },
            Cell: (row) => {
                return (
                    <div className={getTableStatusClassNames(row.row.original.claimStatus)}>
                        <span>{statusFormat(row.row.original.claimStatus)}</span>
                    </div>
                );
            },
            searchFilterValue: "status",
            maxWidth: 80,
        },
    ];

    const onPageChange = (e) => {
        setTableSearchFilter({
            ...tableSearchFilter,
            pageNo: e.selected,
        });
    };

    const tableRowSelected = async (row) => {
        try {
            const response = await api.get.claimManagementClaimDetails(row.id);
            const fileSplitted = response.data.result.filePath.split(".");
            const fileType = fileSplitted[fileSplitted.length - 1];
            setSelectedClaim({ ...response.data.result, fileType: fileType });
            setModalIsOpenSequence(["MODAL_DETAILS"]);
        } catch (error) {
            let sanitizedError = sanitizeError(error);
            appToast(sanitizedError, false);
        }
    };

    return (
        <AppPagesLayout>
            <div className="page-claim-history" onClick={() => setStatusFilterOpen(false)}>
                <AppDualColumnsModal
                    title="Claim Details"
                    isOpenModal={modalIsOpenSequence.slice(-1)[0] === "MODAL_DETAILS"}
                    onRequestClose={() => setModalIsOpenSequence(modalIsOpenSequence.slice(0, -1))}
                    status={statusFormat(selectedClaim.status)}
                    statusColor={getStatusColor(selectedClaim.status ? selectedClaim.status.toLowerCase() : "")}
                    childrenLeft={
                        <div>
                            <div className="page-claim-history__modal-input-wrapper">
                                <AppInputWithLabel placeholder="Username" value={selectedClaim.userName} disabled={true} />
                            </div>
                            <div className="page-claim-history__modal-input-wrapper">
                                <AppInputWithLabel placeholder="Claim Type" value={selectedClaim.claimType} disabled={true} />
                            </div>
                            <div className="page-claim-history__modal-input-wrapper">
                                <span className="page-claim-history__modal-icon-wrapper">
                                    <AppAttachmentImage
                                        fileType={selectedClaim.fileType}
                                        onClick={() => {
                                            setAttachmentSrc({
                                                file: selectedClaim.filePath,
                                                type: selectedClaim.fileType,
                                            });
                                            setModalIsOpenSequence([...modalIsOpenSequence, "MODAL_PDF"]);
                                        }}
                                    />
                                </span>
                                <AppInputWithLabel
                                    placeholder="Attachment"
                                    value={`        ${selectedClaim.attachment}`}
                                    disabled={true}
                                    onClick={() => {
                                        setAttachmentSrc({
                                            file: selectedClaim.filePath,
                                            type: selectedClaim.fileType,
                                        });
                                        setModalIsOpenSequence([...modalIsOpenSequence, "MODAL_PDF"]);
                                    }}
                                />
                            </div>
                            <div className="page-claim-history__modal-input-wrapper">
                                <AppTextArea placeholder="Description" value={selectedClaim.desc} disabled={true} />
                            </div>
                        </div>
                    }
                    childrenRight={
                        <div>
                            <div className="page-claim-history__modal-input-wrapper">
                                <AppInputWithLabel placeholder="Employee ID" value={selectedClaim.staffId} disabled={true} />
                            </div>
                            <div className="page-claim-history__modal-input-wrapper">
                                <AppInputWithLabel placeholder="Bill Amount (RM)" value={selectedClaim.amount} disabled={true} />
                            </div>
                            {statusFormat(selectedClaim.status) === "Approved" ? (
                                <div className="page-claim-history__modal-input-wrapper">
                                    <AppInputWithLabel placeholder="Approved Amount (RM)" value={selectedClaim.approveAmount} disabled={true} />
                                </div>
                            ) : null}
                            <div className="page-claim-history__modal-input-wrapper">
                                <AppInputWithLabel placeholder="Submit Date" value={selectedClaim.submitDate} disabled={true} />
                            </div>
                            {statusFormat(selectedClaim.status) === "Rejected" ? (
                                <Fragment>
                                    <div className="page-claim-history__modal-input-wrapper">
                                        <AppInputWithLabel placeholder="Reject Reason" value={selectedClaim.rejectReason} disabled={true} />
                                    </div>
                                    <div className="page-claim-history__modal-input-wrapper">
                                        <AppTextArea placeholder="Remark" value={selectedClaim.remark} disabled={true} />
                                    </div>
                                </Fragment>
                            ) : null}
                        </div>
                    }
                />
                <AppModal
                    isOpenModal={modalIsOpenSequence.slice(-1)[0] === "MODAL_PDF"}
                    onRequestClose={() => {
                        setModalIsOpenSequence(modalIsOpenSequence.slice(0, -1));
                        setTimeout(() => {
                            setAttachmentSrc(attachmentSrcInitialState);
                        }, 200);
                    }}
                    emptyModal={true}>
                    {attachmentSrc.type === "pdf" ? (
                        <div className="page-claim-history__modal-attachment">
                            <object width="100%" height="100%" data={attachmentSrc.file} type="application/pdf">
                                {" "}
                            </object>
                        </div>
                    ) : (
                        <img className="page-claim-history__modal-image" alt="" src={attachmentSrc.file} />
                    )}
                </AppModal>
                <div className="page-claim-history__breadcrumb">
                    <AppBreadcrumb list={breadcrumb} />
                </div>

                <div className="page-claim-history__user-info">
                    <div className="page-claim-history__user-info--left">
                        <span>{workingStatus.userName}</span>
                        <span className="page-claim-history__user-id">{workingStatus.staffId}</span>
                    </div>
                </div>

                <div className="page-claim-history__tab-wrapper">
                    <AppTabLink tabList={appTabLink} />
                </div>

                <div className="page-claim-history__table-wrapper">
                    <AppReactTable columns={tableColumn} data={tableData.content} onClickRow={(row) => tableRowSelected(row)} />
                </div>
                <div className="page-claim-history__pagination">
                    <AppPaginate onPageChange={onPageChange} pageCount={tableData.totalPages} forcePage={tableSearchFilter.pageNo} />
                </div>
            </div>
        </AppPagesLayout>
    );
};

export default PageClaimHistory;
